import React from "react";
import { graphql } from "react-relay";
import { Link } from "react-router-dom";

import { faCalendar, faSpinnerThird, faThumbsUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact } from "lodash";
import moment from "moment";

import ThemeContext from "~/common/ThemeContext";
import ViewportContext from "~/common/ViewportContext";
import { smart } from "~/lib/relayUtil";
import { Twix } from "~/lib/twix";
import NameList from "~/ui/avatar/NameList";
import Avatar from "~/ui/legacy/Avatar";

import { ReactComponent as ThreeSixtyFeedbackIconDark } from "./css/360-feedback-icon-dark.svg";
import { ReactComponent as ThreeSixtyFeedbackIcon } from "./css/360-feedback-icon.svg";

import type { FeedbackSubject_feedback$data as Feedback } from "~/__generated__/FeedbackSubject_feedback.graphql";
import type { FeedbackSubject_item$data as Item } from "~/__generated__/FeedbackSubject_item.graphql";
import type { FeedbackSubjectRequest_item$data as Request } from "~/__generated__/FeedbackSubjectRequest_item.graphql";
import type { AncestryRelationship } from "~/__generated__/UserPopup_user.graphql";

import styles from "./css/FeedbackSubject.module.css";

export const FeedbackTopicSkeleton = ({ small = false }: { small?: boolean }) => (
	<div className={cx(styles.subjectSkeleton, { [styles.small]: small })}>
		<FontAwesomeIcon spin icon={faSpinnerThird} className={styles.typeIconSkeleton} />
		<div className={styles.textSkeleton}>
			<div className={cx(styles.title, styles.titleSkeleton)} />
			<div className={cx(styles.description, styles.descriptionSkeleton)} />
		</div>
	</div>
);

graphql`
	fragment FeedbackSubject_feedback on FeedbackNode {
		id
		type
		title
		isDraft
		sharedWithMe
		isAnonymous
		creator {
			id
			email
			image
			fullName
			firstName
			lastName
		}

		recipients {
			user {
				id
			}
		}

		context {
			__typename
			... on User {
				id
				email
				image
				fullName
				firstName
				lastName
				isGuest
				relationshipToViewer
			}
			... on Event {
				id
				title
				start
				end
			}
			... on Note {
				id
				title
				eventStart
				eventEnd
				url
			}
			... on OtherContext {
				name
				emoji
			}
		}
	}
`;

interface User {
	id: string;
	fullName: string;
}

interface TypeComponentProps {
	context: Feedback["context"][0];
	isThreeSixtyParent?: boolean;
}

function TypeComponent({ context, isThreeSixtyParent = false }: TypeComponentProps) {
	const { isDarkMode } = React.useContext(ThemeContext);

	if (isThreeSixtyParent) {
		return isDarkMode ? (
			<ThreeSixtyFeedbackIconDark className={styles.typeIcon} />
		) : (
			<ThreeSixtyFeedbackIcon className={styles.typeIcon} />
		);
	}

	if (context) {
		if (context.__typename === "User") {
			return <Avatar user={context} link={false} className={styles.typeIcon} />;
		} else if (context.__typename === "Event" || context.__typename === "Note") {
			return (
				<div className={styles.faIconContainer}>
					<FontAwesomeIcon className={styles.faIcon} icon={faCalendar} />
				</div>
			);
		} else if (context.__typename == "OtherContext") {
			return <div style={{ marginRight: "0.375em", fontSize: "2.25em" }}>{context.emoji}</div>;
		}
	}

	return (
		<div className={styles.faIconContainer}>
			<FontAwesomeIcon className={styles.faIcon} icon={faThumbsUp} />
		</div>
	);
}

function getSpecificTitle(context: Item["context"]) {
	if (!context.length) return null;

	if (context.length === 1) {
		if (context[0].__typename === "Event" || context[0].__typename === "Note") {
			return context[0].title;
		} else if (context[0].__typename === "User") {
			return context[0].fullName;
		} else if (context[0].__typename === "OtherContext") {
			return context[0].name;
		}

		return null;
	}

	const users = compact(context.map(c => (c.__typename === "User" ? c : null)));

	let mainUser = users.find(({ relationshipToViewer }) => relationshipToViewer === "SELF");

	if (!mainUser) {
		mainUser = users.find(
			({ relationshipToViewer }) => relationshipToViewer === "MANAGER" || relationshipToViewer === "DIRECT_REPORT",
		);
	}

	return <NameList selfId={mainUser?.id || users[0]?.id} useSelfName users={users} />;
}

function getTitle({
	type,
	context,
	user,
	isDraft = false,
}: {
	type: Item["type"];
	context: Item["context"];
	user: User;
	isDraft?: boolean;
}) {
	let verb = "Feedback about";
	if (type === "UNSOLICITED" && isDraft) verb = "Give feedback to";

	let subject: string | null = null;
	if (context[0]) {
		if (context[0].__typename === "User") {
			if (context.length > 1) {
				subject = `${context.length} People`;
			} else if (context[0].id === user.id) {
				subject = `myself (${user.fullName})`;
			} else {
				subject = context[0].fullName;
			}
		} else if (context[0].__typename === "Event" || context[0].__typename === "Note") {
			subject = context[0].title;

			const start = context[0].__typename === "Event" ? context[0].start : context[0].eventStart;
			const date = start ? moment(start) : undefined;

			if (date?.isValid()) {
				subject = `${context[0].title} (${date.format("ll")})`;
			}
		} else if (context[0].__typename === "OtherContext") {
			subject = context[0].name;
		}
	}

	subject = subject ? `${subject}` : "...";
	return `${verb} ${subject}`;
}

type FeedbackRequestSubjectProps = React.HTMLProps<HTMLDivElement> & {
	request: Request;
	user: User;
	small?: boolean;
	isSpecific?: boolean;
};

export const FeedbackRequestSubject = smart(
	class FeedbackRequestSubjectClass extends React.PureComponent<FeedbackRequestSubjectProps> {
		static contextType = ViewportContext;
		declare context: React.ContextType<typeof ViewportContext>;

		static Fragments = {
			request: graphql`
				fragment FeedbackSubjectRequest_item on FeedbackRequestNode {
					id
					cancelledByCreator

					feedbackRecord {
						contents
					}

					feedback {
						isAnonymous
						...FeedbackSubject_feedback @relay(mask: false)
					}
				}
			`,
		};

		static defaultProps = {
			small: false,
			isSpecific: false,
		};

		renderType() {
			return <TypeComponent context={this.props.request.feedback.context[0]} />;
		}

		getDescription = (): React.ReactNode => {
			const { request, isSpecific } = this.props;
			const { context } = request.feedback;

			if (isSpecific) {
				if (context.length === 1 && context[0].__typename === "Event") {
					const { start, end } = context[0];
					if (!start || !end) return "";

					const time = new Twix(moment(start), moment(end)).format({
						implicitYear: true,
						implicitMinutes: true,
					});

					return time;
				} else if (context.length === 1 && context[0].__typename === "Note") {
					const { eventStart, eventEnd, url } = context[0];
					if (!eventStart || !eventEnd) return "";

					const time = new Twix(moment(eventStart), moment(eventEnd)).format({
						implicitYear: true,
						implicitMinutes: true,
					});

					return url ? <Link to={url}>{time}</Link> : time;
				}

				return "";
			}

			if (request.feedbackRecord) return "Response sent";
			if (request.cancelledByCreator) return "Request unavailable";

			return "Awaiting my response";
		};

		getTitle() {
			const { user, request, isSpecific } = this.props;
			const { feedback } = request;
			if (feedback.title && !isSpecific) return feedback.title;

			const { type, context } = feedback;

			if (isSpecific) return getSpecificTitle(context) || getTitle({ type, context, user });

			return getTitle({ type, context, user });
		}

		render() {
			const { small, user: _user, isSpecific, className, request, ...divProps } = this.props;
			const title = this.getTitle();

			return (
				<div className={cx(styles.subject, className, { [styles.small]: small })} {...divProps}>
					{this.renderType()}
					<div className={styles.subjectText}>
						<p className={styles.title} title={typeof title === "string" ? title : undefined}>
							{title}
						</p>
						<p className={styles.description}>
							{this.getDescription()}
							{!isSpecific && request.feedback.isAnonymous && " (Anonymous)"}
						</p>
					</div>
				</div>
			);
		}
	},
);

type FeedbackSubjectProps = React.HTMLProps<HTMLDivElement> & {
	feedback: Item;
	user: User;
	small?: boolean;
	isSpecific?: boolean;
	isThreeSixtyParent?: boolean;
};

export const FeedbackSubject = smart(
	class FeedbackSubjectClass extends React.PureComponent<FeedbackSubjectProps> {
		static contextType = ViewportContext;
		declare context: React.ContextType<typeof ViewportContext>;

		static Fragments = {
			feedback: graphql`
				fragment FeedbackSubject_item on FeedbackNode {
					...FeedbackSubject_feedback @relay(mask: false)
				}
			`,
		};

		static defaultProps = {
			small: false,
			isSpecific: false,
			isThreeSixtyParent: false,
		};

		getDescription = (): React.ReactNode => {
			const { user, feedback, isSpecific, isThreeSixtyParent } = this.props;
			const { isDraft, type, creator, sharedWithMe, recipients, context } = feedback;

			if (isThreeSixtyParent) {
				return "360 Feedback";
			}

			if (isSpecific) {
				if (context.length === 1 && context[0].__typename === "Event") {
					const { start, end } = context[0];
					if (!start || !end) return "";

					const time = new Twix(moment(start), moment(end)).format({
						implicitYear: true,
						implicitMinutes: true,
					});

					return time;
				} else if (context.length === 1 && context[0].__typename === "Note") {
					const { eventStart, eventEnd, url } = context[0];
					if (!eventStart || !eventEnd) return "";

					const time = new Twix(moment(eventStart), moment(eventEnd)).format({
						implicitYear: true,
						implicitMinutes: true,
					});

					return url ? <Link to={url}>{time}</Link> : time;
				}

				return "";
			}

			if (type === "UNSOLICITED") {
				if (creator && creator.id === user.id && !recipients.some(({ user }) => user.id === creator.id)) {
					return isDraft ? "Give feedback" : "Given feedback";
				} else if (!sharedWithMe) {
					return "Received feedback";
				}
			}

			if (type === "REQUESTED" && creator && creator.id === user.id) {
				return "";
			}

			if (sharedWithMe) return "Shared with me";

			return "";
		};

		getTitle() {
			const { user, feedback, isSpecific } = this.props;
			if (feedback.title && !isSpecific) return feedback.title;

			const { type, context, isDraft } = feedback;

			if (isSpecific) return getSpecificTitle(context) || getTitle({ type, context, isDraft, user });

			return getTitle({ type, context, isDraft, user });
		}

		renderType() {
			const { isThreeSixtyParent, feedback } = this.props;
			const { context } = feedback;

			if (context.length === 1) return <TypeComponent context={context[0]} isThreeSixtyParent={isThreeSixtyParent} />;

			const isUser = (
				context: Item["context"][0],
			): context is Item["context"][0] & { relationshipToViewer: AncestryRelationship } => {
				return context.__typename === "User";
			};

			const users = context.filter(isUser);

			let mainUser = users.find(({ relationshipToViewer }) => relationshipToViewer === "SELF");

			if (!mainUser) {
				mainUser = users.find(
					({ relationshipToViewer }) => relationshipToViewer === "MANAGER" || relationshipToViewer === "DIRECT_REPORT",
				);
			}

			return <TypeComponent context={mainUser || users[0]} isThreeSixtyParent={isThreeSixtyParent} />;
		}

		render() {
			const {
				small,
				feedback: _feedback,
				user: _user,
				className,
				isSpecific: _isSpecific,
				isThreeSixtyParent: _isThreeSixtyParent,
				...divProps
			} = this.props;
			const title = this.getTitle();

			return (
				<div className={cx(styles.subject, className, { [styles.small]: small })} {...divProps}>
					{this.renderType()}
					<div className={styles.subjectText}>
						<p className={styles.title} title={typeof title === "string" ? title : undefined}>
							{title}
						</p>
						<p className={styles.description}>
							{this.getDescription()}
							{this.props.feedback.isAnonymous ? " (Anonymous)" : ""}
						</p>
					</div>
				</div>
			);
		}
	},
);
